import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import { useLocation } from "react-router-dom";
import { Form, Formik } from "formik";

import { Button, Flex, Grid, GridItem } from "@chakra-ui/react";

import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import RelatorioService from "../../../main/services/RelatorioService";
import { Permission, criancaFunc } from "../../../helpers/Funcionalidades";

import { useAuth } from "react-oidc-context";
import CustomSelect from "../CustomSelect";

export const FiltersFilaProcessamento = ({
  entidade,
  data,
  setData,
  setLoading,
  roleKeycloack,
  user,
  exclusao,
  setExclusao,
  atualizarDadosTabela,
  setAtualizarDadosTabela,
  loading,
  setSelectedRows,
  atualizarFilaProcessamento,
  setAtualizarFilaProcessamento,
  verificarProcessamento,
}) => {
  const auth = useAuth();
  const formRef = useRef(null);
  const location = useLocation();
  const initialLoad = useRef(true);
  const token = auth?.user?.access_token;

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  const statusOptions = useMemo(
    () => [
      { label: "Concluído com sucesso", value: "CONCLUIDO_COM_SUCESSO" },
      { label: "Aguardando processamento", value: "AGUARDANDO_PROCESSAMENTO" },
      { label: "Erro processamento", value: "ERRO_DE_PROCESSAMENTO" },
    ],
    []
  );

  const tipoOptions = useMemo(() => {
    const options = [
      {
        label: "Relatório de Serviço de Acolhimento Básico",
        value: "RELATORIO_SERVICOS_ACOLHIMENTOS_BASICO",
      },
      {
        label: "Relatório de Serviço de Acolhimento Completo",
        value: "RELATORIO_SERVICOS_ACOLHIMENTOS_COMPLETO",
      },
      {
        label: "Relatório de Crianças e Adolescentes Básico",
        value: "RELATORIO_CRIANCA_ADOLESCENTES_BASICO",
      },
      {
        label: "Relatório de Crianças e Adolescentes Completo",
        value: "RELATORIO_CRIANCA_ADOLESCENTES_COMPLETO",
      },
      {
        label: "Lista de Pendências de Crianças e Adolescentes",
        value: "LISTA_PENDENCIAS_CRIANCA_ADOLESCENTES",
      },
      {
        label: "Lista de Pendências Serviço de Acolhimento",
        value: "LISTA_PENDENCIAS_SERVICO_ACOLHIMENTO",
      },
      {
        label: "Relatório de Pendências de Crianças e Adolescentes",
        value: "RELATORIO_PENDENCIAS_ACOLHIDOS",
      },
      {
        label: "Relatório de Pendências Serviço de Acolhimento",
        value: "RELATORIO_PENDENCIAS_SERVICOS_ACOLHIMENTO",
      },
    ];

    return options.sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
    );
  }, []);

  const sortOptions = useMemo(
    () => [
      { label: "Mais Recente", value: "DESC" },
      { label: "Mais Antigo", value: "ASC" },
    ],
    []
  );

  const [initialValues, setInitialValues] = useState({
    nomeResponsavel: "",
    statusRelatorio: "",
    tipoRelatorio: "",
    sortType: sortOptions[0],
    pageSize: 10,
    page: 0,
  });

  useEffect(() => {
    const saved = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    if (saved) {
      setInitialValues({
        nomeResponsavel: saved.nomeResponsavel || "",
        statusRelatorio:
          statusOptions.find((opt) => opt.value === saved.statusRelatorio) ||
          null,
        tipoRelatorio:
          tipoOptions.find((opt) => opt.value === saved.tipoRelatorio) || null,
        sortType: sortOptions.find((opt) => opt.value === saved.sortType) || {
          label: "Mais Recente",
          value: "DESC",
        },
        pageSize: saved.pageSize || 10,
        page: saved.page || 0,
      });
    }
  }, [entidade, statusOptions, tipoOptions, sortOptions]);

  useEffect(() => {
    if (!initialLoad.current) {
      if (formRef.current) {
        formRef.current.handleSubmit();
      }
    } else {
      initialLoad.current = false;
    }
  }, [location]);

  const funcPesquisarFilaProcessamento = useCallback(
    async (valuesToSend) => {
      const res = await RelatorioService.getFilaProcessamento(
        token,
        valuesToSend
      );

      const currentPage = valuesToSend.page ?? 0;
      const isPageEmpty = res.data.content.length === 0;
      const hasElements = res.data.totalElements > 0;

      if (isPageEmpty && hasElements && currentPage > 0) {
        const newPage = currentPage - 1;
        const newValues = { ...valuesToSend, page: newPage };

        const res2 = await RelatorioService.getFilaProcessamento(
          token,
          newValues
        );

        sessionStorage.setItem(
          entidade + "-filter",
          JSON.stringify({
            ...newValues,
            totalItems: res2.data.totalElements,
            totalPages: res2.data.totalPages,
          })
        );

        setData(res2.data);
        setLoading(false);
        return res2;
      }

      sessionStorage.setItem(
        entidade + "-filter",
        JSON.stringify({
          ...valuesToSend,
          totalItems: res.data.totalElements,
          totalPages: res.data.totalPages,
        })
      );

      setData(res.data);
      setLoading(false);
      return res;
    },
    [entidade, setData, setLoading, token]
  );

  useEffect(() => {
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    const pesquisou = sessionStorage.getItem(entidade + "-filterSearch");
    const passou = sessionStorage.getItem("crianca-passou");

    if (atualizarDadosTabela || (pesquisou && passou)) {
      setLoading(true);

      const valuesToSend = {
        nomeResponsavel: storage?.nomeResponsavel
          ? storage?.nomeResponsavel
          : "",
        statusRelatorio: storage?.statusRelatorio
          ? storage?.statusRelatorio.value
          : "",
        tipoRelatorio: storage?.tipoRelatorio
          ? storage?.tipoRelatorio.value
          : "",
        sortType: storage?.sortType
          ? storage?.sortType.value || storage?.sortType
          : "DESC",
        pageSize: storage?.pageSize ? storage.pageSize : "",
        page: storage?.page ? storage.page : "",
      };

      funcPesquisarFilaProcessamento(valuesToSend).finally(() => {
        if (atualizarFilaProcessamento) {
          verificarProcessamento();
          setAtualizarFilaProcessamento(false);
        }
        setAtualizarDadosTabela(false);
      });
    }
  }, [
    exclusao,
    atualizarDadosTabela,
    setAtualizarDadosTabela,
    location.state,
    entidade,
    funcPesquisarFilaProcessamento,
    atualizarFilaProcessamento,
    verificarProcessamento,
    setAtualizarFilaProcessamento,
    setLoading,
  ]);

  useEffect(() => {
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));

    const normalizedStorage = {
      ...storage,
      sortType: storage?.sortType?.value || storage?.sortType || "DESC",
    };

    sessionStorage.setItem(
      entidade + "-filter",
      JSON.stringify(normalizedStorage)
    );

    const valuesToSend = {
      nomeResponsavel: storage?.nomeResponsavel || "",
      statusRelatorio: storage?.statusRelatorio
        ? storage.statusRelatorio.value
        : "",
      tipoRelatorio: storage?.tipoRelatorio ? storage.tipoRelatorio.value : "",
      sortType: normalizedStorage?.sortType
        ? normalizedStorage.sortType
        : "DESC",
      pageSize: storage?.pageSize || 10,
      page: storage?.page || 0,
    };

    setLoading(true);
    funcPesquisarFilaProcessamento(valuesToSend);
  }, [entidade, funcPesquisarFilaProcessamento, setLoading]);

  const handleSubmit = (values, actions) => {
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    setTimeout(() => {
      const { nomeResponsavel, statusRelatorio, tipoRelatorio, sortType } =
        values;

      values.page = 0;
      values.pageSize = storage?.pageSize ? storage.pageSize : 10;

      const valuesToSend = {
        nomeResponsavel: nomeResponsavel ? nomeResponsavel : "",
        statusRelatorio: statusRelatorio ? statusRelatorio.value : "",
        tipoRelatorio: tipoRelatorio ? tipoRelatorio.value : "",
        sortType: sortType ? sortType.value : "DESC",
        pageSize: storage?.pageSize ? storage.pageSize : 10,
        page: 0,
      };
      setLoading(true);

      sessionStorage.setItem(
        entidade + "-filter",
        JSON.stringify(valuesToSend)
      );
      if (entidade === "fila-processamento") {
        funcPesquisarFilaProcessamento(valuesToSend);
        sessionStorage.removeItem("selectedRows");
        setSelectedRows([]);
      }
      actions.setSubmitting(false);
    }, 200);
  };

  return (
    <Flex flexDir="column" alignItems="center" w="100%">
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        {(props) => (
          <Form style={{ width: "100%" }}>
            <Flex flexDirection="column" alignItems="center">
              <Grid
                templateColumns={{
                  base: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(12, 2fr)",
                }}
                rowGap="10px"
                columnGap="18px"
                w="100%"
                mt="2px"
              >
                <>
                  <GridItem colSpan={{ base: 2, sm: 2, lg: 4 }}>
                    <CustomSelect
                      fieldName="tipoRelatorio"
                      mt={0}
                      value={props.values.tipoRelatorio || ""}
                      placeholder="Pesquisar por Nome do Relatório"
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      options={tipoOptions}
                      onChange={(value) => {
                        props.setFieldValue("tipoRelatorio", value);
                      }}
                    />
                  </GridItem>
                  {/* <GridItem colSpan={{ base: 2, sm: 2, lg: 3 }}>
                    <Field name="nomeResponsavel">
                      {({ field, form }) => (
                        <Input
                          {...field}
                          h="42px"
                          name="nomeResponsavel"
                          id="search-nomeResponsavel"
                          backgroundColor="white"
                          autoComplete="off"
                          placeholder="Pesquise por Nome do Solicitante"
                        />
                      )}
                    </Field>
                  </GridItem> */}

                  <GridItem colSpan={{ base: 2, sm: 2, lg: 4 }}>
                    <CustomSelect
                      fieldName="statusRelatorio"
                      mt={0}
                      value={props.values.statusRelatorio || ""}
                      placeholder="Selecione o Status"
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      options={statusOptions}
                      onChange={(value) => {
                        props.setFieldValue("statusRelatorio", value);
                      }}
                    />
                  </GridItem>
                  <GridItem colSpan={{ base: 2, sm: 2, lg: 4 }}>
                    <CustomSelect
                      fieldName="sortType"
                      mt={0}
                      value={props.values.sortType || ""}
                      placeholder="Ordernar por..."
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      options={sortOptions}
                      onChange={(value) => {
                        props.setFieldValue("sortType", value);
                      }}
                    />
                  </GridItem>
                </>
              </Grid>

              <Permission
                roleKeycloack={roleKeycloack}
                func={criancaFunc.pesquisar}
              >
                <Button
                  type="submit"
                  translate="no"
                  color="#FFF"
                  backgroundColor="#169499"
                  fontWeight={700}
                  fontSize="15px"
                  lineHeight="21.6px"
                  _hover={{ bg: "#169499" }}
                  isLoading={props.isSubmitting}
                  mt={6}
                  w={"100%"}
                  maxW={{ base: "100%", md: "50%", lg: "17%" }}
                >
                  Pesquisar
                </Button>
              </Permission>
            </Flex>
          </Form>
        )}
      </Formik>
    </Flex>
  );
};
